import { useState } from "react";
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { Checkbox } from '@/shared/components/Checkbox'
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import tabletImage from "@/assets/tablet-login.webp";
import bigLogo from '@/assets/big-logo.svg';
import RecoveryPassword from '@/pages/auth/RecoveryPassword';

interface LoginProps {
  onLogin: (
    email: string,
    password: string
  ) => Promise<{ firstName: string; lastName: string } | void>;
  isLoading: boolean;
  error: string | null;
}

const Login = ({ onLogin, isLoading, error }: LoginProps) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [typeForm, setTypeForm] = useState<"login" | "recovery">("login");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (localError) setLocalError(null);
    if (successMessage) setSuccessMessage(null);
  };

  const getErrorMessage = (err: unknown): string => {
    if (err && typeof err === "object" && "message" in err) {
      const errorObj = err as {
        message: string | string[];
        statusCode?: number;
      };

      if (Array.isArray(errorObj.message)) {
        const messages = errorObj.message.map((msg) => {
          if (msg.toLowerCase().includes("email"))
            return "El correo electrónico no es válido";
          if (msg.toLowerCase().includes("password"))
            return "La contraseña no cumple los requisitos";
          return msg;
        });
        return messages.join(". ");
      }

      const message = errorObj.message as string;

      if (
        message.toLowerCase().includes("invalid credentials") ||
        message.toLowerCase().includes("credenciales inválidas") ||
        message.toLowerCase().includes("unauthorized")
      ) {
        return "Credenciales incorrectas. Por favor verifica tu correo y contraseña.";
      }

      if (
        message.toLowerCase().includes("user not found") ||
        message.toLowerCase().includes("usuario no encontrado")
      ) {
        return "El usuario no existe. Por favor verifica tu correo electrónico.";
      }

      if (
        message.toLowerCase().includes("account is disabled") ||
        message.toLowerCase().includes("account disabled") ||
        message.toLowerCase().includes("cuenta deshabilitada") ||
        message.toLowerCase().includes("user is not active")
      ) {
        return "Tu cuenta está deshabilitada. Por favor contacta al administrador.";
      }

      if (
        message.toLowerCase().includes("too many attempts") ||
        message.toLowerCase().includes("demasiados intentos")
      ) {
        return "Demasiados intentos fallidos. Por favor espera unos minutos e intenta nuevamente.";
      }

      if (errorObj.statusCode === 401) {
        return "Credenciales incorrectas. Por favor verifica tu correo y contraseña.";
      }

      if (errorObj.statusCode === 403) {
        return "No tienes permisos para acceder al sistema. Contacta al administrador.";
      }

      if (errorObj.statusCode === 500) {
        return "Error del servidor. Por favor intenta nuevamente en unos momentos.";
      }

      if (message && message.length > 0 && message.length < 200) {
        return message;
      }

      return "Error al iniciar sesión. Por favor intenta nuevamente.";
    }

    return "Error inesperado al iniciar sesión. Por favor verifica tu conexión e intenta nuevamente.";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);

    try {
      const userProfile = await onLogin(formData.email, formData.password);

      let welcomeMessage = "¡Bienvenido de nuevo!";
      if (userProfile && "firstName" in userProfile) {
        const firstName = userProfile.firstName;
        welcomeMessage = `¡Bienvenido de nuevo, ${firstName}!`;
      }

      setSuccessMessage(welcomeMessage);
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      setLocalError(message);
      console.error("🚨 Error de login:", err);
    }
  };

  const displayError = error || localError;

  return (
    <div className="h-screen bg-white flex overflow-hidden">
      {/* Left Side - Form Section */}
      {typeForm == "login" && (
        <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
          <div className="w-full max-w-md">
            {/* Logo - Centrado y Grande */}
            <div className="mb-8 flex flex-col items-center">
              <img
                src={bigLogo}
                alt="SLYRSA Logo"
                className="h-20 w-auto mb-2"
              />
            </div>

            {/* Welcome Text */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Accede a tu cuenta
              </h1>
              <p className="text-sm text-gray-600">
                Ingresa tu correo y contraseña para iniciar sesión
              </p>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-green-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">
                      {successMessage}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {displayError && !successMessage && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                  <p className="ml-3 text-sm text-red-800">{displayError}</p>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Field */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-900 mb-2"
                >
                  Correo electrónico
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border-x-0 border-t-0 border-b-1 border-[#787A7D] rounded-none focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                  placeholder="correo@example.com"
                />
              </div>

              {/* Password Field */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-900 mb-2"
                >
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border-x-0 border-t-0 border-b-1 border-[#787A7D] rounded-none focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                    placeholder="••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember Me */}
              <div className="flex items-center">
                <Checkbox
                  id="remember-me"
                  name="remember-me"
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-sm text-gray-700"
                >
                  Recordar contraseña
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className={estiloDeBoton('primario', 'w-full')}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Iniciando sesión...
                  </div>
                ) : (
                  "Iniciar sesión"
                )}
              </button>
            </form>

            {/* Forgot Password Link */}
            <div className="mt-4 text-center">
              <p
                onClick={() => setTypeForm("recovery")}
                className="text-sm text-[#3B5998] hover:text-[#344e86] transition-colors cursor-pointer"
              >
                ¿Olvidaste tu contraseña?
              </p>
            </div>
          </div>
        </div>
      )}

      {typeForm == "recovery" && <RecoveryPassword setTypeForm={setTypeForm} />}

      {/* Right Side - Features Section with Gradient in Rounded Box */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-6 bg-gray-50">
        {/* Rounded White Container */}
        <div className="bg-gradient-to-br from-[#8B7EC8] via-[#6B8DD6] to-[#5B9FE3] rounded-[2.5rem] p-10 w-full h-[calc(100vh-3rem)] flex flex-col items-center justify-between relative overflow-hidden shadow-xl">
          {/* Features List - Más abajo */}
          <div className="relative z-10 w-full max-w-lg space-y-4 mt-12">
            {/* Feature 1 */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-[2rem] p-4 flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-5 h-5 bg-green-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white text-sm leading-relaxed">
                Facilita, monitorea y da seguimiento a la selección de personal.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-[2rem] p-4 flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-5 h-5 bg-green-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white text-sm leading-relaxed">
                Centraliza todo el proceso de reclutamiento, desde la solicitud
                de vacantes hasta la contratación final.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-[2rem] p-4 flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-5 h-5 bg-green-500 rounded-md flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-white text-sm leading-relaxed">
                Optimiza la comunicación entre áreas y agiliza la toma de
                decisiones en cada etapa del proceso de selección.
              </p>
            </div>
          </div>

          {/* Tablet Image - Alineado al fondo */}
          <div className="relative z-10 w-full max-w-2xl flex items-end justify-center flex-shrink-0 mt-auto">
            <img
              src={tabletImage}
              alt="Dashboard en tablet"
              className="relative sm:top-[40px] top-[18px] w-full h-auto drop-shadow-2xl object-contain"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
